import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context
import { AuthProvider } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Staff from './pages/Staff';
import Classes from './pages/Classes';
import Attendance from './pages/Attendance';
import Fees from './pages/Fees';
import Exams from './pages/Exams';
import ExamSetup from './pages/ExamSetup';
import SubjectAllocation from './pages/SubjectAllocation';
import MarksEntry from './pages/MarksEntry';
import ExamMonitoring from './pages/ExamMonitoring';
import StudentReport from './pages/StudentReport';
import Library from './pages/Library';
import Transport from './pages/Transport';
import Holidays from './pages/Holidays';
import StaffApproval from './pages/StaffApproval';
import LeaveManagement from './pages/LeaveManagement';
import StaffAttendance from './pages/StaffAttendance';
import Certificates from './pages/Certificates';
import Reports from './pages/Reports';
import BirthdayDashboard from './pages/BirthdayDashboard';
import StudentFeeReport from './pages/StudentFeeReport';
import Notices from './pages/Notices';
import OnlineAdmission from './pages/OnlineAdmission';
import AdmissionForm from './pages/AdmissionForm';
import CumulativeReport from './pages/CumulativeReport';
import Settings from './pages/Settings';
import StudentSummary from './pages/StudentSummary';
import AttendanceCalendar from './pages/AttendanceCalendar';
import Roles from './pages/admin/Roles';
import Hostel from './pages/Hostel';
import Inventory from './pages/Inventory';
import Payroll from './pages/Payroll';
import Letters from './pages/Letters';
import PrintIdCard from './pages/PrintIdCard';
import TimetableSettings from './pages/TimetableSettings';
import Timetable from './pages/Timetable';
import EnquiryDashboard from './pages/EnquiryDashboard';
import AttendanceMonitoring from './pages/AttendanceMonitoring';
import InwardOutward from './pages/InwardOutward';
import PrintStaffIdCard from './pages/PrintStaffIdCard';
import SettingsManagement from './pages/SettingsManagement';
import StudentAnalytics from './pages/StudentAnalytics';
import TransportFeeReport from './pages/TransportFeeReport';
import SMSCommunication from './pages/SMSCommunication';
import ExamManagement from './pages/ExamManagement';
import AppDownloadMonitor from './pages/AppDownloadMonitor';
import AutoRenewal from './pages/AutoRenewal';
import PaymentChallan from './pages/PaymentChallan';
import RefundableDeposit from './pages/RefundableDeposit';
import Accounts from './pages/Accounts';
import BonafideSettings from './pages/BonafideSettings';
import ProspectFees from './pages/ProspectFees';
import FeeReminder from './pages/FeeReminder';
import GeneralRegister from './pages/GeneralRegister';
import VisitorManagement from './pages/VisitorManagement';
import Scholarship from './pages/Scholarship';
import DataVerification from './pages/DataVerification';
import ExamResultPrint from './pages/ExamResultPrint';
import MaintenanceSettings from './pages/MaintenanceSettings';

// Components
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';

// Create modern premium theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1a9e8f',
      light: '#4db6ac',
      dark: '#158a7d',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    error: {
      main: '#e53935',
    },
    warning: {
      main: '#ff9800',
    },
    success: {
      main: '#4caf50',
    },
    background: {
      default: '#f0f2f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a2332',
      secondary: '#5a6a7e',
    },
  },
  typography: {
    fontFamily: "'Inter', 'Cambay', 'Segoe UI', Roboto, Arial, sans-serif",
    h4: { fontWeight: 800, letterSpacing: -0.5 },
    h5: { fontWeight: 700, letterSpacing: -0.3 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    body2: { lineHeight: 1.6 },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 700,
          boxShadow: 'none',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)', transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.06)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'box-shadow 0.2s ease',
            '&:hover': { boxShadow: '0 2px 8px rgba(26, 158, 143, 0.08)' },
            '&.Mui-focused': { boxShadow: '0 2px 12px rgba(26, 158, 143, 0.12)' },
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          backgroundColor: '#f8f9fa',
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 6,
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        }
      }
    }
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="students" element={<Students />} />
              <Route path="students/auto-renewal" element={<AutoRenewal />} />
              <Route path="staff" element={<Staff />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="classes" element={<Classes />} />
              <Route path="fees" element={<Fees />} />
              <Route path="exams" element={<Exams />} />
              <Route path="exams/setup" element={<ExamSetup />} />
              <Route path="exams/subject-allocation" element={<SubjectAllocation />} />
              <Route path="exams/marks-entry" element={<MarksEntry />} />
              <Route path="exams/monitoring" element={<ExamMonitoring />} />
              <Route path="exams/student-report" element={<StudentReport />} />
              <Route path="library" element={<Library />} />
              <Route path="holidays" element={<Holidays />} />
              <Route path="transport" element={<Transport />} />
              <Route path="certificates" element={<Certificates />} />
              <Route path="reports" element={<Reports />} />
              <Route path="reports/birthdays" element={<BirthdayDashboard />} />
              <Route path="reports/fees" element={<StudentFeeReport />} />
              <Route path="communication/notices" element={<Notices />} />
              <Route path="admission/online" element={<OnlineAdmission />} />
              <Route path="admission/form" element={<AdmissionForm />} />
              <Route path="reports/cumulative" element={<CumulativeReport />} />
              <Route path="settings" element={<Settings />} />
              <Route path="student-summary" element={<StudentSummary />} />
              <Route path="staff/approval" element={<StaffApproval />} />
              <Route path="staff/leave" element={<LeaveManagement />} />
              <Route path="staff/attendance" element={<StaffAttendance />} />
              <Route path="attendance/calendar" element={<AttendanceCalendar />} />
              <Route path="roles" element={<Roles />} />
              <Route path="hostel" element={<Hostel />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="payroll" element={<Payroll />} />
              <Route path="letters" element={<Letters />} />
              <Route path="idcards" element={<PrintIdCard />} />
              <Route path="timetable/settings" element={<TimetableSettings />} />
              <Route path="timetable" element={<Timetable />} />
              <Route path="enquiry" element={<EnquiryDashboard />} />
              <Route path="attendance/monitoring" element={<AttendanceMonitoring />} />
              <Route path="inward-outward" element={<InwardOutward />} />
              <Route path="staff/idcards" element={<PrintStaffIdCard />} />
              <Route path="settings/masters" element={<SettingsManagement />} />
              <Route path="analytics/students" element={<StudentAnalytics />} />
              <Route path="transport/reports" element={<TransportFeeReport />} />
              <Route path="sms" element={<SMSCommunication />} />
              <Route path="exams/management" element={<ExamManagement />} />
              <Route path="monitoring/app-downloads" element={<AppDownloadMonitor />} />
              <Route path="payment-challan" element={<PaymentChallan />} />
              <Route path="refundable-deposits" element={<RefundableDeposit />} />
              <Route path="accounts" element={<Accounts />} />
              <Route path="certificates/settings" element={<BonafideSettings />} />
              <Route path="fees/prospect" element={<ProspectFees />} />
              <Route path="fees/reminder" element={<FeeReminder />} />
              <Route path="general-register" element={<GeneralRegister />} />
              <Route path="visitors" element={<VisitorManagement />} />
              <Route path="scholarship" element={<Scholarship />} />
              <Route path="data-verification" element={<DataVerification />} />
              <Route path="exams/result-print" element={<ExamResultPrint />} />
              <Route path="settings/maintenance" element={<MaintenanceSettings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
